import { KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CountryOptions, CountrySearch } from "../../../../../../components/Form/PhoneNumberInput/styles";
import { localeOf } from "../../../../../../services/i18ln/format";
import { classNames } from "../../../../../../utils/styles";
import { Dropdown, Field, Option } from "./styles";

// The currencies most Keepos sell in: on top of the list, before the alphabetical rest
const PRIORITY = ["BRL", "USD", "EUR"];

const normalize = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

type Currency = { code: string; name: string; symbol: string };

/** A currency named and with its symbol in the language; one the browser can't name is shown by its code. */
const describeCurrency = (code: string, locale: string, names?: Intl.DisplayNames): Currency => {
  try {
    return {
      code,
      name: (names ?? new Intl.DisplayNames(locale, { type: "currency" })).of(code) || code,
      symbol:
        new Intl.NumberFormat(locale, { style: "currency", currency: code })
          .formatToParts(1)
          .find(part => part.type === "currency")?.value || code,
    };
  } catch {
    return { code, name: code, symbol: code };
  }
};

const currenciesByLocale = new Map<string, Currency[]>();

/**
 * Every currency the browser knows, named and with its symbol in the language of the editor. Built once for
 * each language (about 300 of them), not at every visit to the screen.
 */
export const currenciesIn = (language: string): Currency[] => {
  const locale = localeOf(language);
  const cached = currenciesByLocale.get(locale);
  if (cached) {
    return cached;
  }
  const codes: string[] = Intl.supportedValuesOf?.("currency") ?? PRIORITY;
  const names = new Intl.DisplayNames(locale, { type: "currency" });
  const all = codes.map(code => describeCurrency(code, locale, names));
  const list = [
    ...PRIORITY.flatMap(code => all.filter(currency => currency.code === code)),
    ...all.filter(currency => !PRIORITY.includes(currency.code)).sort((a, b) => a.name.localeCompare(b.name, locale)),
  ];
  currenciesByLocale.set(locale, list);
  return list;
};

type Props = {
  label: string;
  value: string;
  onChange: (code: string) => void;
};

/** The currency of the section: a field that opens a list of every currency, with a search (name or code). */
export const CurrencySelect = ({ label, value, onChange }: Props) => {
  const { t, i18n } = useTranslation();
  const listId = useId();
  const container = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);

  const [isOpen, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const listed = useMemo(() => currenciesIn(i18n.language), [i18n.language]);
  // the currency the section has, when the browser doesn't list it, goes on top: it stays the chosen one
  const unlisted = useMemo(
    () =>
      listed.some(currency => currency.code === value) ? undefined : describeCurrency(value, localeOf(i18n.language)),
    [listed, value, i18n.language]
  );
  const currencies = useMemo(() => (unlisted ? [unlisted, ...listed] : listed), [unlisted, listed]);
  const selected = currencies.find(currency => currency.code === value) || currencies[0];
  // the first of the rest, after the currencies most Keepos sell in: where the list draws a line
  const dividedAt = currencies.findIndex(currency => currency !== unlisted && !PRIORITY.includes(currency.code));

  const filtered = useMemo(() => {
    const search = normalize(query.trim());
    return search
      ? currencies.filter(({ code, name }) => normalize(name).includes(search) || code.toLowerCase().includes(search))
      : currencies;
  }, [query, currencies]);

  // closes on a click outside it
  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);

  // keeps the highlighted option in sight while moving with the arrows (or opening, or searching), but not
  // when the mouse moves it: scrolling then would slide another option under the pointer
  const scrollToActive = useRef(false);
  useEffect(() => {
    if (isOpen && scrollToActive.current) {
      list.current?.children[active]?.scrollIntoView?.({ block: "nearest" });
    }
    scrollToActive.current = false;
  }, [active, isOpen]);

  const open = () => {
    setQuery("");
    scrollToActive.current = true;
    setActive(Math.max(0, currencies.indexOf(selected)));
    setOpen(true);
  };

  // closed by the keyboard, the focus goes back to the field
  const close = () => {
    setOpen(false);
    field.current?.focus();
  };

  const pick = (code: string) => {
    onChange(code);
    close();
  };

  const keyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      scrollToActive.current = true;
      setActive(Math.min(active + 1, filtered.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      scrollToActive.current = true;
      setActive(Math.max(active - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (filtered[active]) pick(filtered[active].code);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "Tab") {
      // the focus goes on, to the next field: the list closes behind it
      setOpen(false);
    }
  };

  return (
    <Field ref={container}>
      <button
        ref={field}
        type="button"
        className="value"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${label}: ${selected.name}`}
        onClick={() => (isOpen ? setOpen(false) : open())}>
        <span className="symbol">{selected.symbol}</span>
        <span className="name">{selected.name}</span>
        <span className="code">{selected.code}</span>
      </button>
      {isOpen && (
        <Dropdown>
          <CountrySearch
            type="search"
            role="combobox"
            aria-expanded
            aria-autocomplete="list"
            aria-activedescendant={filtered[active] ? `${listId}-${filtered[active].code}` : undefined}
            autoFocus
            aria-label={t("pages.editor.pricing.form.currency.search")}
            aria-controls={listId}
            placeholder={t("pages.editor.pricing.form.currency.search")}
            value={query}
            onChange={event => {
              setQuery(event.target.value);
              scrollToActive.current = true;
              setActive(0);
            }}
            onKeyDown={keyDown}
          />
          <CountryOptions>
            <ul id={listId} ref={list} role="listbox" aria-label={label}>
              {filtered.length ? (
                filtered.map(({ code, name, symbol }, index) => (
                  <Option
                    key={code}
                    id={`${listId}-${code}`}
                    role="option"
                    aria-selected={code === value}
                    className={classNames({ divided: !query && index === dividedAt })}
                    $active={index === active}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => pick(code)}>
                    <span className="symbol">{symbol}</span>
                    <span className="name">{name}</span>
                    <span className="code">{code}</span>
                  </Option>
                ))
              ) : (
                <li className="empty">{t("pages.editor.pricing.form.currency.none")}</li>
              )}
            </ul>
          </CountryOptions>
        </Dropdown>
      )}
    </Field>
  );
};
